import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormState } from 'react-use-form-state'
import { toast } from 'react-toastify'
import { connect } from 'react-redux'

import capitalize from '../../common/formatters/capitalize'
import toPlural from '../../common/formatters/plural'

import S from './styled'
import { DeleteIcon } from '../icons'
import Input from '../input'
import Validator from '../../common/validators/exists'
import ItemInput from './item'

import { Loading, ConfirmModal } from '../'

import { fetchItems, addItem, deleteItem, updateItem } from './actions'

const SimpleEntitiesEditor = ({
  items, loading,
  fetchItems, addItem, deleteItem, updateItem,
  entityName, entityLabel,
  children,
}) => {
  const [formState, { text, label }] = useFormState({ name: '', items })
  if (items !== formState.values.items) {
    formState.setField('items', items)
  }

  useEffect(() => {
    (async () => {
      await fetchItems()
    })()
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  const [showModal, setShowModal] = useState(null)

  const onAdd = useCallback(
    event => {
      event.preventDefault()
      if (!formState.errors.name && formState.values.name.trim()) {
        addItem(formState.values.name.trim()).then(() => {
          toast.success(`${capitalize(entityLabel || entityName)} '${formState.values.name}' added`, {
            position: toast.POSITION.TOP_RIGHT,
          })
          formState.reset()
        })
      }
      return false
    },
    [addItem, entityLabel, entityName, formState],
  )

  return loading ? <Loading overlay/> : (
    <S.Content>
      <S.List>
        { items.map((item, index) => (
          <S.Locale key={item.id}>
            <ItemInput {...{
              item,
              items,
              updateItem,
              entityName,
              entityLabel,
            }}/>
            <S.DeleteLink onClick={
              () => setShowModal({ id: item.id })
            }>
              <DeleteIcon />
            </S.DeleteLink>
          </S.Locale>
        ))}
        <form onSubmit={onAdd} autoComplete="off" style={{ maxWidth: '250px' }}>
          <Input {...{
            title: `New ${(entityLabel || entityName).toLocaleLowerCase()}`,
            name: 'name',
            text, label,
            onAdd,
            error: formState.errors.name,
            validate: Validator,
            validateOnBlur: false,
            autoFocus: true,
            inverted: true,
          }}/>
        </form>
      </S.List>
      {
        children && children
      }
      <ConfirmModal
        isOpened={showModal}
        onClose={() => setShowModal(null)}
        onConfirm={() => {
          deleteItem(showModal.id)
          setShowModal(null)
        }}
        redConfirm
      >
        Are you sure you want to delete this item?
      </ConfirmModal>

    </S.Content>
  )
}

SimpleEntitiesEditor.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  fetchItems: PropTypes.func,
  addItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
}

const mapStateToProps = (state, { entityName }) => {
  return ({
    items: state[toPlural(entityName)].list,
    loading: state[toPlural(entityName)].isLoading,
  })
}

const mapDispatchToProps = (dispatch, { entityName } ) => ({
  fetchItems: () => dispatch(fetchItems(entityName)()),
  addItem: name => dispatch(addItem(entityName)(name)),
  deleteItem: entityId => dispatch(deleteItem(entityName)(entityId)),
  updateItem: (entityId, name) => dispatch(updateItem(entityName)(entityId, name)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SimpleEntitiesEditor)
